// "use client"; // If using Next.js App Router

import { createContext, useContext, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

// --- 1. Context Definition ---
// We define the shape of the data that our context will provide to its children.
// This acts as a "public API" for other components to interact with the editor modal.
type AssetEditorContextType = {
  /** Opens the global asset editor modal for a specific asset. */
  openEditor: (assetId: string, assetName: string, assetType: string) => void;
  /** Closes the global asset editor modal. */
  closeEditor: () => void;
};

/**
 * Creates the React Context. Components will use this to access the provider's state and functions.
 * It's initialized as undefined because it's expected to be used within the Provider's scope.
 */
const AssetEditorContext = createContext<AssetEditorContextType | undefined>(undefined);

// --- 2. Provider Component ---
/**
 * The AssetEditorProvider is a wrapper component that holds the state and logic for the editor modal.
 * By wrapping our application with it, we make the editor functionality available globally.
 */
export const AssetEditorProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();

  // Function to navigate to the asset console view instead of opening modal
  const openEditor = (id: string, name: string, type: string) => {
    navigate(`/console/assets/${id}`);
  };
  
  // Function to close (navigate back)
  const closeEditor = () => {
    // Optionally navigate back to asset list or wherever appropriate
    navigate('/console/assets');
  };

  return (
    // The Provider makes the `openEditor` and `closeEditor` functions available to any child component.
    <AssetEditorContext.Provider value={{ openEditor, closeEditor }}>
      {children}
    </AssetEditorContext.Provider>
  );
};

// --- 3. Custom Hook ---
/**
 * A custom hook to simplify accessing the context.
 * It provides a clean API (`useAssetEditor()`) and includes an error check
 * to ensure the component using it is wrapped within an AssetEditorProvider.
 */
export const useAssetEditor = () => {
  const context = useContext(AssetEditorContext);
  if (!context) {
    throw new Error('useAssetEditor must be used within an AssetEditorProvider');
  }
  return context;
};


